import React, { useRef, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  Box
} from "@mui/material";
import { CameraAlt as CameraIcon, Close as CloseIcon } from "@mui/icons-material";
import { toast } from 'react-toastify';
interface CameraDialogProps {
  open: boolean;
  onClose: () => void;
  onCapture: (imageData: { 
    uri: string; 
    type: string; 
    name: string;
    file: File;
  }) => void;
  capturedImage: string | null;
  setCapturedImage: (image: string | null) => void;
}
const CameraDialog: React.FC<CameraDialogProps> = ({
  open,
  onClose,
  onCapture,
  capturedImage,
  setCapturedImage
}) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const startCamera = async () => {
    try {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
      }
      const mediaStream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: "environment" }
      });
      await new Promise(resolve => {
        if (videoRef.current) {
          videoRef.current.onloadedmetadata = () => {
            resolve(true);
          };
          videoRef.current.srcObject = mediaStream;
          streamRef.current = mediaStream;
        } else {
          resolve(false);
        }
      });
    } catch (err) {
      toast.error("Could not access the camera. Please check permissions.");
      onClose();
    }
  };
  const stopCamera = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
  };
  const capturePhoto = () => {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (!video || !canvas) return;
    
    const context = canvas.getContext("2d");
    if (!context) {
      console.error("Could not get canvas context");
      return;
    }
  
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    context.drawImage(video, 0, 0, canvas.width, canvas.height);
    canvas.toBlob((blob) => {
      if (!blob) return;
      
      const file = new File([blob], `odometer_${Date.now()}.jpg`, { 
        type: "image/jpeg" 
      });
      
      const imageData = {
        uri: URL.createObjectURL(blob),
        type: file.type,
        name: file.name,
        file: file 
      };
      
      setCapturedImage(imageData.uri);
      stopCamera();
    }, "image/jpeg", 0.9);
  };

  useEffect(() => {
    if (open) {
      startCamera();
    } else {
      stopCamera();
    }
    return () => {
      stopCamera();
    };
  }, [open]);
  const dataURLtoFile = (dataUrl: string, filename: string): File => {
    const arr = dataUrl.split(",");
    const mime = arr[0].match(/:(.*?);/)?.[1] || "image/jpeg";
    const bstr = atob(arr[1]);
    let n = bstr.length;
    const u8arr = new Uint8Array(n);
  
    while (n--) {
      u8arr[n] = bstr.charCodeAt(n);
    }
  
    return new File([u8arr], filename, { type: mime });
  };
  
  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        padding: '5px',
        backgroundColor: 'white',
        boxShadow: 1,
        fontWeight: 'bold'
      }}>
        Take Odometer Photo
        <IconButton onClick={onClose} sx={{ position: 'absolute', right: 8, top: 8 }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Box sx={{ 
          display: 'flex', 
          flexDirection: 'column', 
          alignItems: 'center',
          minHeight: '300px',
          backgroundColor: '#f5f5f5'
        }}>
          {!capturedImage ? (
            <video 
              ref={videoRef} 
              autoPlay 
              playsInline 
              style={{ 
                width: '100%', 
                maxHeight: '60vh',
                backgroundColor: '#000'
              }}
            />
          ) : (
            <img 
              src={capturedImage} 
              alt="Captured odometer" 
              style={{ 
                width: '100%', 
                maxHeight: '60vh',
                objectFit: 'contain'
              }} 
            />
          )}
          <canvas ref={canvasRef} style={{ display: 'none' }} />
        </Box>
      </DialogContent>
      <DialogActions sx={{ justifyContent: 'center' }}>
        {!capturedImage ? (
          <Button 
            variant="contained" 
            color="primary" 
            onClick={capturePhoto}
            startIcon={<CameraIcon />}
            sx={{ mb: 2 }}
          >
            Capture
          </Button>
        ) : (
          <>
            <Button 
              variant="outlined" 
              color="secondary" 
              onClick={() => setCapturedImage(null)}
              sx={{ mr: 2 }}
            >
              Retake
            </Button>
            <Button 
              variant="contained" 
              color="primary" 
              onClick={() => {
                if (capturedImage) {
                  canvasRef.current?.toBlob((blob) => {
                    if (!blob) return;
                    
                    const file = new File([blob], `odometer_${Date.now()}.jpg`, {
                      type: "image/jpeg"
                    });
                    
                    const imageData = {
                      uri: URL.createObjectURL(blob),
                      type: file.type,
                      name: file.name,
                      file: file
                    };
        
                      onCapture(imageData);
                      onClose();
                    }, "image/jpeg", 0.9);
                  }
                }}
              >
                Use Photo
              </Button>
                        </>
                      )}
                    </DialogActions>
                  </Dialog>
                );
              };

              export default CameraDialog;